import { Button, Divider, MenuItem, TextField } from '@mui/material';
import { blue } from '@mui/material/colors';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '../../../../trpc/react';
import { MultiSelect } from '../../../components/input/multi-select';
import { SearchInput } from '../../../components/input/search-input';
import { SchemeCard } from '../../library/schemes/components/SchemeCard';
import { FocusOption, Scheme } from '../../library/schemes/types';
import { AdvancedSearchModal } from './AdvancedSearchModal';
import { AdvancedSearchTable } from './AdvancedSearchTable';

export const AdvancedSchemeSearchModal = ({
  exerciseId,
  open,
  onClose,
  onAddScheme,
}: {
  exerciseId: string;
  open: boolean;
  onClose: () => void;
  onAddScheme: (scheme: Scheme) => void;
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [focuses, setFocuses] = useState<FocusOption[]>([]);
  const [cycles, setCycles] = useState<number | undefined>(undefined);
  const [trainingTypeId, setTrainingTypeId] = useState<string | undefined>(
    undefined,
  );

  const focusesQuery = trpc.schemeLibrary.trainingFocuses.useQuery(undefined, {
    staleTime: 10_000,
  });
  const exerciseQuery = trpc.exerciseLibrary.exerciseById.useQuery(
    { libraryExerciseId: exerciseId },
    {
      staleTime: 10_000,
    },
  );
  const schemesQuery = trpc.schemeLibrary.searchSchemes.useQuery(
    {
      searchTerm,
      focusIds: focuses.map((f) => f.id),
      numOfCycles: cycles,
      trainingTypeId,
    },
    {
      staleTime: 10_000,
    },
  );
  const trainingTypesQuery = trpc.trainingType.fetch.useQuery({});

  const focusOptions = focusesQuery.data || [];
  const trainingTypes = trainingTypesQuery.data || [];
  const exercise = exerciseQuery.data;
  const schemes = schemesQuery.data?.records ?? [];

  return exercise ? (
    <AdvancedSearchModal<Scheme>
      id={exerciseId}
      modalTitle={t('addSchemeFor', { exerciseName: exercise.name })}
      galleryTitle={t('schemes')}
      open={open}
      onClose={onClose}
      onAddItem={onAddScheme}
      items={schemes}
      isLoading={schemesQuery.isLoading}
      isError={schemesQuery.isError}
      renderCard={(item, viewMode, isSelected, setSelected) => {
        return (
          <SchemeCard
            key={item.id}
            sx={isSelected ? { borderColor: blue[500], borderWidth: 2 } : {}}
            item={item}
            focusOptions={focusOptions}
            viewMode={viewMode}
            onClick={setSelected}
          />
        );
      }}
      renderTableView={(items, selectedItem, setSelected) => (
        <AdvancedSearchTable<Scheme>
          items={items}
          setSelected={setSelected}
          selectedItem={selectedItem}
          onAddItem={onAddScheme}
          title={t('schemes')}
        />
      )}
      filters={
        <>
          <SearchInput
            fullWidth
            value={searchTerm}
            onChange={(v) => setSearchTerm(v)}
          />
          <Divider sx={{ my: 1, mb: 1 }} flexItem />
          <TextField
            label={t('exerciseType')}
            select
            value={trainingTypeId ?? ''}
            onChange={(e) => setTrainingTypeId(e.target.value || undefined)}
          >
            {trainingTypes.map((tt) => {
              return (
                <MenuItem value={tt.id} key={tt.id}>
                  {tt.name}
                </MenuItem>
              );
            })}
          </TextField>
          <TextField
            label={t('numberOfCycles')}
            type="number"
            value={cycles}
            onChange={(e) => {
              const value = e.target.value;
              setCycles(value ? parseInt(value) : undefined);
            }}
          />
          <MultiSelect
            label={t('trainingFocus')}
            placeholder={t('all')}
            selectedOptions={focuses}
            options={focusOptions}
            onChange={setFocuses}
            optionToName={(option) => option.name}
          />
          <Button
            disableRipple
            size="small"
            variant="text"
            sx={{
              color: 'highEmphasisTextLight',
              padding: 0,
              justifyContent: 'flex-start',
              '&:hover': {
                boxShadow: 'none',
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
            }}
            onClick={() => {
              setSearchTerm('');
              setFocuses([]);
              setCycles(undefined);
              setTrainingTypeId(undefined);
            }}
          >
            {t('resetFilters')}
          </Button>
        </>
      }
    />
  ) : null;
};
